// WebSocket client with cursor resync.
//
// The server owns runs, so this socket is a notification channel over
// authoritative database state — never a delivery guarantee. Two consequences
// shape the code below:
//
//   - on connect we send our per-conversation cursors and the server replays
//     what we missed, so reconnect, cold start and multi-device are one path
//   - the server drops us if we fall behind, so a closed socket is normal and
//     reconnecting must be cheap and automatic

import { authToken } from './auth'

export type EventType =
  | 'message.created'
  | 'part.delta'
  | 'part.done'
  | 'message.done'
  | 'run.status'
  | 'approval.requested'
  | 'approval.decided'
  | 'conversation.updated'
  | 'contact.presence'
  | 'typing'
  | 'resync'

export interface Envelope {
  type: EventType
  conversation_id?: string
  message_id?: string
  run_id?: string
  seq?: number
  at: number
  data?: unknown
}

export interface PartDelta {
  index: number
  kind: string
  text: string
}

type Handler = (e: Envelope) => void

export class RealtimeClient {
  private ws: WebSocket | null = null
  private handlers = new Set<Handler>()
  private cursors = new Map<string, number>()
  private reconnectAttempt = 0
  private closed = false
  private reconnectTimer: number | null = null

  /** onStatus reports connection state so the UI can show an offline banner. */
  onStatus: (connected: boolean) => void = () => {}

  connect() {
    this.closed = false
    void this.open()
  }

  private async open() {
    if (this.closed) return

    // A browser cannot set headers on a WebSocket handshake, so the token goes
    // in the query string — the one place Parley accepts that, and the reason
    // Clerk's short token lifetime matters (see auth.TokenQueryParam server
    // side). It is minted per connection attempt, so a socket reopened after an
    // hour authenticates with a current token rather than a stale one.
    const token = await authToken()
    if (this.closed) return

    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const cursors = [...this.cursors.entries()].map(([id, seq]) => `${id}:${seq}`).join(',')

    const params = new URLSearchParams()
    if (cursors) params.set('since', cursors)
    if (token) params.set('token', token)
    const query = params.toString()
    const url = `${proto}//${location.host}/api/v1/ws${query ? `?${query}` : ''}`

    const ws = new WebSocket(url)
    this.ws = ws

    ws.onopen = () => {
      this.reconnectAttempt = 0
      this.onStatus(true)
    }

    ws.onmessage = (ev) => {
      let envelope: Envelope
      try {
        envelope = JSON.parse(ev.data as string)
      } catch {
        return
      }

      // Track the high-water mark per conversation; this is what we hand back
      // on reconnect to get exactly what we missed.
      if (envelope.conversation_id && typeof envelope.seq === 'number' && envelope.seq > 0) {
        const prev = this.cursors.get(envelope.conversation_id) ?? 0
        if (envelope.seq > prev) this.cursors.set(envelope.conversation_id, envelope.seq)
      }

      for (const h of this.handlers) h(envelope)
    }

    ws.onclose = () => {
      this.onStatus(false)
      this.scheduleReconnect()
    }

    ws.onerror = () => ws.close()
  }

  private scheduleReconnect() {
    if (this.closed || this.reconnectTimer !== null) return

    // Exponential backoff with jitter, capped: being dropped for falling behind
    // is routine, and a reconnect storm would make it worse.
    const base = Math.min(30_000, 500 * 2 ** this.reconnectAttempt)
    const delay = base * (0.5 + Math.random() / 2)
    this.reconnectAttempt++

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      void this.open()
    }, delay)
  }

  /** setCursor seeds the high-water mark from messages fetched over REST. */
  setCursor(conversationId: string, seq: number) {
    const prev = this.cursors.get(conversationId) ?? 0
    if (seq > prev) this.cursors.set(conversationId, seq)
  }

  /** getCursor exposes the last envelope seq we actually applied for a
   *  conversation. A targeted resync (EventResync — the server dropping us
   *  for being too slow on one conversation, not the whole socket) needs to
   *  refetch from exactly this point, the same number a full reconnect would
   *  send as `since`. */
  getCursor(conversationId: string): number {
    return this.cursors.get(conversationId) ?? 0
  }

  /** markRead tells the server the user is looking at this conversation. */
  markRead(conversationId: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'read', conversation_id: conversationId }))
    }
  }

  subscribe(h: Handler): () => void {
    this.handlers.add(h)
    return () => this.handlers.delete(h)
  }

  close() {
    this.closed = true
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.ws?.close()
    this.ws = null
  }
}

export const realtime = new RealtimeClient()
